import { Close, Notifications } from '@mui/icons-material';
import { Box, IconButton, Paper } from '@mui/material';
import Container from '@mui/material/Container';
import Popover from '@mui/material/Popover';
import React, { Fragment, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import WarningComponent from '/@/components/notification/warning';
import NotificationsComponent from '/@/components/notifications';
import { _ } from '/@/locale';
import { getAppInstance } from '/@/store/session/selectors';
import useIsMobile from '/@/utils/hooks/is-mobile';
import useIsPlaywriteEnvironment from '/@/utils/hooks/is-playwrite-environment';
import { SidebarButton } from '../../app-navbar/sidebar';
import { useIconNotification } from './useIconNotification';

const IconNotification: React.FC<{ variant?: 'sidebar' | 'bottombar' }> = ({ variant = 'sidebar' }) => {
    const appInstance = useSelector(getAppInstance);
    const isMobile = useIsMobile();
    const isPlaywriteEnvironment = useIsPlaywriteEnvironment();

    const {
        anchorEl,
        unreadedNotifications,
        readedNotifications,
        open,
        id,
        shouldShowNotificationIcon,
        showWarningCard,
        handleClick,
        handleClose,
        dismissWarning,
        markNotificationAsRead,
        markNotificationAsClicked,
        markAllAsRead,
    } = useIconNotification();

    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        // Add to url so other views are aware of the notification icon open
        const newSearchParams = new URLSearchParams(searchParams);
        if (open) {
            newSearchParams.set('notificationOpen', 'true');
        } else {
            newSearchParams.delete('notificationOpen');
        }
        setSearchParams(newSearchParams, { replace: true });
    }, [open]);

    if (isPlaywriteEnvironment) return null;

    return (
        <Fragment>
            {shouldShowNotificationIcon &&
                (variant === 'sidebar' ? (
                    <SidebarButton
                        key={'notifications'}
                        tab={{
                            label: _('Avisos'),
                            icon: (
                                <Box>
                                    <Notifications />
                                    {Boolean(unreadedNotifications.length) && (
                                        <Box
                                            sx={{
                                                borderRadius: '50%',
                                                width: '8px',
                                                height: '8px',
                                                backgroundColor: 'red',
                                                position: 'absolute',
                                                top: '10px',
                                                right: '12px',
                                                zIndex: 1,
                                                outline: '2px solid white',
                                            }}
                                        />
                                    )}
                                </Box>
                            ),
                            onClick: (e: React.MouseEvent<HTMLElement>) => {
                                handleClick(e);
                            },
                        }}
                    />
                ) : (
                    <IconButton
                        onClick={(e: React.MouseEvent<HTMLElement>) => {
                            handleClick(e);
                        }}
                        color='primary'
                        size='small'
                    >
                        <Box>
                            <Notifications />
                            {Boolean(unreadedNotifications.length) && (
                                <Box
                                    sx={{
                                        borderRadius: '50%',
                                        width: '8px',
                                        height: '8px',
                                        backgroundColor: 'red',
                                        position: 'absolute',
                                        top: '4px',
                                        right: '4px',
                                        zIndex: 1,
                                        outline: '2px solid white',
                                    }}
                                />
                            )}
                        </Box>
                    </IconButton>
                ))}

            {appInstance.enableNotifications && (
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    id={id}
                    role='dialog'
                    aria-labelledby='notifications-title'
                    aria-describedby='notifications-description'
                    sx={{
                        '.MuiPopover-paper': {
                            backgroundColor: 'white',
                            color: 'black',
                            padding: 0,
                            margin: 0,
                            minWidth: { xs: '100vw', md: 400 },
                            maxWidth: { xs: '100vw', md: 400 },
                            maxHeight: 600,
                            minHeight: { md: 'auto' },
                            overflow: 'scroll',
                            border: '0.5px solid #cccc',
                            boxShadow: 'none',
                            '&::-webkit-scrollbar': {
                                display: 'none',
                            },
                            // Pin the panel to a viewport edge so it can't drift off-screen as its
                            // content height changes (e.g. expanding the VTPRO4 warning). Mobile sticks
                            // to the top (full-screen sheet); desktop sticks to the bottom and grows up.
                            ...(isMobile ? { top: '0px !important' } : { top: 'auto !important', bottom: 0 }),
                        },
                    }}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    anchorReference={!isMobile ? 'anchorPosition' : 'none'}
                    anchorPosition={!isMobile ? { top: 0, left: 100 } : undefined}
                >
                    <Container disableGutters maxWidth={{ xs: '100vw', md: 400 }}>
                        <NotificationsComponent
                            unreadedNotifications={unreadedNotifications}
                            readedNotifications={readedNotifications}
                            handleClose={handleClose}
                            markAllAsRead={markAllAsRead}
                            markNotificationAsRead={markNotificationAsRead}
                            markNotificationAsClicked={markNotificationAsClicked}
                        />
                    </Container>
                </Popover>
            )}

            {showWarningCard && (
                <Paper
                    elevation={6}
                    role='dialog'
                    aria-labelledby='notification-warning-title'
                    sx={{
                        position: 'fixed',
                        zIndex: (theme) => theme.zIndex.snackbar,
                        overflow: 'hidden',
                        borderRadius: 1,
                        ...(isMobile
                            ? { left: 8, right: 8, bottom: 8 }
                            : { right: 16, bottom: 16, width: 360, maxWidth: 'calc(100vw - 32px)' }),
                    }}
                >
                    <IconButton
                        aria-label={_('Cerrar')}
                        size='small'
                        onClick={dismissWarning}
                        sx={{ position: 'absolute', top: 4, right: 4, color: 'white', zIndex: 1 }}
                    >
                        <Close fontSize='small' />
                    </IconButton>
                    <WarningComponent handleCloseIconNotification={dismissWarning} closePopover={dismissWarning} />
                </Paper>
            )}
        </Fragment>
    );
};

export default React.memo(IconNotification);
